import { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import Header from './Header'
import Navigation from './Navigation'
import TopNavigation from './TopNavigation'
import Footer from './Footer'
import LicenseBanner from './LicenseBanner'
import { clientNavigation, vendorNavigation } from '../config/navigationConfig'
import { useAuth } from '../contexts/AuthContext'
import { PageActionProvider } from '../contexts/PageActionContext'
import toast from 'react-hot-toast'
import { openEventStream } from '../utils/sseConnect'

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const { user, activePortal } = useAuth()

  useEffect(() => {
    try {
      sessionStorage.setItem('erp:returnTo', `${location.pathname}${location.search || ''}${location.hash || ''}`)
    } catch (_) { void 0 }
  }, [location.pathname, location.search, location.hash])

  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (!token || !user?.id) return

    let disconnect = () => {}

    const setup = async () => {
      const { es, disconnect: close } = await openEventStream({ authToken: token })
      if (!es) return
      disconnect = close

    const onTicketCreated = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        const actorId = envelope?.meta?.actor?.id
        if (actorId && Number(actorId) === Number(user.id)) return
        const ref = envelope?.data?.ticket_number || envelope?.data?.ticket_id || 'ticket'
        const expected = envelope?.data?.response_time ? `${Number(envelope.data.response_time) / 60} hrs` : null
        toast.success(`Ticket #${ref} created${expected ? ` (SLA response: ${expected})` : ''}`)
      } catch (e) { void e }
    }

    const onSlaBreached = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        const ref = envelope?.data?.ticket_number || envelope?.data?.ticket_id || 'ticket'
        const kind = envelope?.data?.kind === 'resolution' ? 'Resolution' : 'Response'
        toast.error(`${kind} SLA breached for Ticket #${ref}`)
      } catch (e) { void e }
    }

    const onTicketAssigned = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        const actorId = envelope?.meta?.actor?.id
        if (actorId && Number(actorId) === Number(user.id)) return
        const ref = envelope?.data?.ticket_number || envelope?.data?.ticket_id || 'ticket'
        const level = envelope?.data?.support_level || ''
        toast(`Ticket #${ref} assigned${level ? ` (${level})` : ''}`)
      } catch (e) { void e }
    }

    const onTicketEscalated = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        const actorId = envelope?.meta?.actor?.id
        if (actorId && Number(actorId) === Number(user.id)) return
        const ref = envelope?.data?.ticket_number || envelope?.data?.ticket_id || 'ticket'
        const toLevel = envelope?.data?.to_level || ''
        toast.error(`Ticket #${ref} escalated${toLevel ? ` to ${toLevel}` : ''}`)
      } catch (e) { void e }
    }

    const onTicketResolved = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        const actorId = envelope?.meta?.actor?.id
        if (actorId && Number(actorId) === Number(user.id)) return
        const ref = envelope?.data?.ticket_number || envelope?.data?.ticket_id || 'ticket'
        toast.success(`Ticket #${ref} resolved`)
      } catch (e) { void e }
    }

    es.addEventListener('ticket.created', onTicketCreated)
    es.addEventListener('ticket.assigned', onTicketAssigned)
    es.addEventListener('ticket.escalated', onTicketEscalated)
    es.addEventListener('ticket.resolved', onTicketResolved)
    es.addEventListener('sla.breached', onSlaBreached)
    }

    setup()

    return () => {
      disconnect()
    }
  }, [user?.id])

  // Select navigation based on active portal context
  const activeNavigation = useMemo(() => {
    if (activePortal === 'vendor') {
      return vendorNavigation
    }
    return clientNavigation
  }, [activePortal])

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === href
    }
    return location.pathname.startsWith(href)
  }

  const getPageContext = () => {
    const crumbs = [{ label: 'Home', to: '/dashboard' }]
    const qs = new URLSearchParams(location.search || '')

    if (location.pathname === '/settings/system' && qs.get('tab') === 'users') {
      return {
        title: 'Users',
        description: 'Manage users',
        breadcrumbs: [
          { label: 'Home', to: '/dashboard' },
          { label: 'Settings', to: '/settings' },
          { label: 'System Settings', to: '/settings/system' },
          { label: 'Users', to: '/settings/system?tab=users' }
        ]
      }
    }
    
    // 1. Check local extra items
    const extraNavigation = [
      {
        name: 'Usage Logs',
        href: '/usage-logs',
        description: 'Track PO usage history'
      }
    ]
    
    const extraMatch = extraNavigation.find(item => isActive(item.href))
    if (extraMatch) {
      crumbs.push({ label: extraMatch.name, to: extraMatch.href })
      return { 
        title: extraMatch.name,
        description: extraMatch.description,
        breadcrumbs: crumbs
      }
    }
    
    // 2. Check active navigation
    for (const item of activeNavigation) {
      if (item.children) {
        // Check direct children
        const childMatch = item.children.find(child => isActive(child.href))
        if (childMatch) {
          if (item.href && item.href !== '#') {
             crumbs.push({ label: item.name, to: item.href })
          } else {
             crumbs.push({ label: item.name, to: '#' }) // Non-clickable parent
          }
          crumbs.push({ label: childMatch.name, to: childMatch.href })
          return {
            title: childMatch.headerTitle || childMatch.name,
            description: childMatch.description,
            showDescriptionBelow: childMatch.showDescriptionBelow,
            fullWidth: childMatch.fullWidth,
            breadcrumbs: crumbs
          }
        }

        // Check grandchildren
        for (const child of item.children) {
          if (child.children) {
            const grandChildMatch = child.children.find(gc => isActive(gc.href))
            if (grandChildMatch) {
              crumbs.push({ label: item.name, to: '#' })
              crumbs.push({ label: child.name, to: '#' })
              crumbs.push({ label: grandChildMatch.name, to: grandChildMatch.href })
              
              return {
                title: grandChildMatch.headerTitle || grandChildMatch.name,
                description: grandChildMatch.description,
                showDescriptionBelow: grandChildMatch.showDescriptionBelow,
                fullWidth: grandChildMatch.fullWidth,
                breadcrumbs: crumbs
              }
            }
          }
        }
      }
      
      if (isActive(item.href)) {
        if (item.href !== '/dashboard') {
          crumbs.push({ label: item.name, to: item.href })
        }
        return {
          title: item.headerTitle || item.name,
          description: item.description,
          showDescriptionBelow: item.showDescriptionBelow,
          fullWidth: item.fullWidth,
          breadcrumbs: crumbs
        }
      }
    }
    
    return { 
      title: 'Dashboard', 
      headerTitle: 'Procurement Overview', 
      description: '', 
      breadcrumbs: crumbs 
    }
  }

  const pageContext = getPageContext()

  return (
    <PageActionProvider>
      <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
        {/* Global License Warning Banner */}
        <LicenseBanner />
        
        {/* Top Navigation Bar (NetSuite Style) */}
        <TopNavigation onMobileMenuOpen={() => setSidebarOpen(true)} />

        {/* Mobile Navigation Sidebar (Drawer) */}
        <Navigation 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          isCollapsed={false}
          toggleCollapse={() => {}}
          mobileOnly={true}
        />

        {/* Main Content Area */}
        <div className="flex flex-col flex-1 overflow-hidden relative z-0">
          {/* Page Context Header */}
          <Header 
            breadcrumbs={pageContext.breadcrumbs}
            title={pageContext.title}
            description={pageContext.description}
            showDescriptionBelow={pageContext.showDescriptionBelow}
          />

          {/* Scrollable Content */}
          <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 overflow-y-auto stylish-scrollbar">
            <div className="w-full">
              {children}
            </div>
          </main>

          <Footer />
        </div>
      </div>
    </PageActionProvider>
  )
}
